"use client";

import { Button } from "@/components/shadcn/button";
import { ArrowRight, Heart, FileText, Users, Clock, CheckCircle2, Phone, MessageSquare, Baby, Stethoscope, Home } from "lucide-react";
import { HeroSection } from "@/components/landing/hero-section";
import { AuthActions } from "@/components/landing/auth-actions";

export default function ReferralLandingPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50/50 to-sky-50">
      {/* Gentle background elements */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-gradient-to-br from-emerald-300/20 to-teal-300/20 blur-3xl animate-pulse" />
        <div className="absolute top-1/2 -left-40 h-[400px] w-[400px] rounded-full bg-gradient-to-tr from-sky-300/20 to-emerald-300/20 blur-3xl animate-pulse delay-1000" />
        <div className="absolute -bottom-40 right-1/3 h-[450px] w-[450px] rounded-full bg-gradient-to-tl from-teal-300/20 to-sky-300/20 blur-3xl animate-pulse delay-500" />
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-white/60 bg-white/60 backdrop-blur-xl">
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between px-4 sm:px-6 py-4 sm:py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg shadow-emerald-500/30">
              <Heart className="h-6 w-6 text-white" />
            </div>
            <div className="leading-tight">
              <div className="text-xl font-bold text-gray-900">Connectory</div>
              <div className="text-xs font-medium text-emerald-600">Health</div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <a href="tel:1-000-000-0000" className="hidden items-center gap-2 text-sm font-medium text-gray-700 transition-colors hover:text-emerald-600 md:flex">
              <Phone className="h-4 w-4" />
              <span>1-000-000-0000</span>
            </a>
            <AuthActions />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <HeroSection />

      {/* Who Can Refer Section */}
      <section className="relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-6 py-8 sm:py-12">
        <div className="mb-8 md:mb-10 text-center">
          <h2 className="mb-3 text-3xl font-bold text-gray-900 sm:text-4xl">
            Who Can Make a Referral?
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-gray-600">
            Anyone who has concerns about a child's development can make a referral
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
          {[
            {
              icon: Home,
              title: "Parents & Guardians",
              description: "You know your child best. Trust your instincts and reach out if you have concerns.",
              color: "from-emerald-500 to-emerald-600"
            },
            {
              icon: Stethoscope,
              title: "Pediatricians & Doctors",
              description: "Medical professionals can refer children who may benefit from developmental support.",
              color: "from-teal-500 to-teal-600"
            },
            {
              icon: Baby,
              title: "Childcare Providers",
              description: "Daycare staff, teachers, and caregivers who work closely with young children.",
              color: "from-sky-500 to-sky-600"
            },
            {
              icon: Users,
              title: "Family Members",
              description: "Grandparents, relatives, or other family members concerned about development.",
              color: "from-emerald-600 to-teal-600"
            },
            {
              icon: MessageSquare,
              title: "Social Workers",
              description: "Social service professionals supporting families and children in the community.",
              color: "from-teal-600 to-sky-600"
            },
            {
              icon: Heart,
              title: "Anyone Concerned",
              description: "Friends, neighbors, or community members who care about a child's wellbeing.",
              color: "from-sky-600 to-emerald-600"
            }
          ].map((who, idx) => (
            <div
              key={idx}
              className="group overflow-hidden rounded-2xl border border-white/60 bg-white/70 p-6 shadow-lg backdrop-blur-sm transition-all hover:scale-105 hover:shadow-xl"
            >
              <div className={`mb-4 inline-flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br ${who.color} shadow-lg`}>
                <who.icon className="h-7 w-7 text-white" />
              </div>
              <h3 className="mb-2 text-lg font-bold text-gray-900">{who.title}</h3>
              <p className="text-sm leading-relaxed text-gray-600">{who.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* When to Refer Section */}
      <section id="when-to-refer" className="relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-6 py-10 sm:py-16">
        <div className="overflow-hidden rounded-3xl border border-white/60 bg-white/70 shadow-2xl backdrop-blur-sm">
          <div className="p-5 sm:p-8 md:p-12">
            <div className="mb-8 text-center">
              <h2 className="mb-4 text-3xl font-bold text-gray-900 sm:text-4xl">
                When Should You Make a Referral?
              </h2>
              <p className="mx-auto max-w-3xl text-lg text-gray-600">
                Early Intervention can help if you notice any concerns in these areas. Trust your instincts—it's always better to ask.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {[
                {
                  category: "Physical Development",
                  concerns: [
                    "Not sitting, crawling, or walking when expected",
                    "Stiff or floppy muscles",
                    "Difficulty with coordination or balance",
                    "Trouble with grasping objects or feeding"
                  ]
                },
                {
                  category: "Communication & Speech",
                  concerns: [
                    "Not babbling or making sounds by 12 months",
                    "Not using words by 18 months",
                    "Difficulty understanding simple directions",
                    "Limited eye contact or response to name"
                  ]
                },
                {
                  category: "Social & Emotional",
                  concerns: [
                    "Not smiling or showing joy",
                    "Avoiding eye contact or interaction",
                    "Difficulty with transitions or changes",
                    "Extreme reactions to sounds, textures, or lights"
                  ]
                },
                {
                  category: "Cognitive & Learning",
                  concerns: [
                    "Not interested in toys or play",
                    "Difficulty with problem-solving for their age",
                    "Not imitating actions or sounds",
                    "Limited curiosity or exploration"
                  ]
                }
              ].map((area, idx) => (
                <div
                  key={idx}
                  className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-white to-emerald-50/30 p-6 shadow-sm"
                >
                  <h3 className="mb-4 text-xl font-bold text-gray-900">{area.category}</h3>
                  <ul className="space-y-2.5">
                    {area.concerns.map((concern, cidx) => (
                      <li key={cidx} className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-emerald-600 mt-0.5" />
                        <span className="text-sm text-gray-700">{concern}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="mt-8 rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-6 text-center">
              <p className="text-base font-semibold text-emerald-900">
                Remember: You don't need a diagnosis to make a referral. If you're concerned, reach out.
              </p>
              <p className="mt-2 text-sm text-emerald-700">
                A free evaluation will help determine if your child can benefit from services.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-6 py-10 sm:py-16">
        <div className="mb-12 text-center">
          <h2 className="mb-4 text-3xl font-bold text-gray-900 sm:text-4xl">
            What Happens After You Refer?
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-gray-600">
            The referral process is simple, supportive, and designed to help your child thrive
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[
            {
              step: "1",
              title: "Submit a Referral",
              description: "Fill out a simple online form or call us. It takes just 10-15 minutes and includes basic information about your child.",
              icon: FileText
            },
            {
              step: "2",
              title: "We'll Contact You",
              description: "A service coordinator will reach out within 2 business days to answer questions and schedule an evaluation.",
              icon: Phone
            },
            {
              step: "3",
              title: "Free Evaluation",
              description: "A team of specialists will evaluate your child's development in a comfortable, family-friendly setting.",
              icon: Users
            },
            {
              step: "4",
              title: "Get Support",
              description: "If eligible, we'll create a personalized plan and connect you with therapists and resources at no cost to you.",
              icon: Heart
            }
          ].map((step, idx) => (
            <div
              key={idx}
              className="relative overflow-hidden rounded-2xl border border-white/60 bg-white/70 p-6 shadow-lg backdrop-blur-sm transition-all hover:shadow-xl"
            >
              <div className="mb-4 flex items-center justify-between">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-600 to-teal-600 text-xl font-bold text-white shadow-lg">
                  {step.step}
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                  <step.icon className="h-5 w-5" />
                </div>
              </div>
              <h3 className="mb-2 text-lg font-bold text-gray-900">{step.title}</h3>
              <p className="text-sm leading-relaxed text-gray-600">{step.description}</p>
              {idx < 3 && (
                <div className="absolute -right-3 top-1/2 hidden -translate-y-1/2 lg:block">
                  <ArrowRight className="h-6 w-6 text-emerald-300" />
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Why Early Intervention Matters */}
      <section className="relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-6 py-10 sm:py-16">
        <div className="overflow-hidden rounded-3xl border border-white/60 bg-gradient-to-br from-emerald-600 to-teal-600 shadow-2xl">
          <div className="px-5 py-12 sm:px-8 sm:py-16 md:px-12 text-center">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm">
              <Heart className="h-8 w-8 text-white" />
            </div>
            <h2 className="mb-4 text-3xl font-bold text-white sm:text-4xl">
              Early Support Makes a Lasting Difference
            </h2>
            <p className="mx-auto mb-8 max-w-3xl text-lg text-emerald-50">
              Research shows that children who receive Early Intervention services show greater improvement in their development and are more likely to reach their full potential. The earlier support begins, the better the outcomes.
            </p>
            <div className="mx-auto grid max-w-3xl grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-6">
              {[
                { stat: "Birth-3 years", label: "Critical window for brain development" },
                { stat: "100% Free", label: "No cost to families" },
                { stat: "Expert Care", label: "Trained specialists" }
              ].map((item, idx) => (
                <div key={idx} className="rounded-xl bg-white/10 p-4 backdrop-blur-sm">
                  <div className="mb-1 text-2xl font-bold text-white">{item.stat}</div>
                  <div className="text-sm text-emerald-50">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-6 py-12 sm:py-20">
        <div className="overflow-hidden rounded-3xl border-2 border-emerald-200 bg-white shadow-xl">
          <div className="px-5 py-12 sm:px-8 sm:py-16 md:px-12 text-center">
            <h2 className="mb-4 text-3xl font-bold text-gray-900 sm:text-4xl">
              Ready to Take the First Step?
            </h2>
            <p className="mx-auto mb-8 max-w-2xl text-lg text-gray-600">
              Making a referral is quick, free, and confidential. You can complete it online in about 10 minutes, or call us for assistance.
            </p>
            <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Button
                size="lg"
                className="w-full sm:w-auto h-16 bg-gradient-to-r from-emerald-600 to-teal-600 px-10 text-lg font-semibold shadow-xl shadow-emerald-500/30 hover:shadow-2xl hover:shadow-emerald-500/40"
                onClick={() => (window.location.href = "/referral")}
              >
                Start Your Referral
                <ArrowRight className="ml-2 h-6 w-6" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto h-16 border-2 px-10 text-lg font-semibold"
                onClick={() => (window.location.href = "tel:1-000-000-0000")}
              >
                <Phone className="mr-2 h-5 w-5" />
                Call 1-000-000-0000
              </Button>
            </div>
            <p className="mt-6 text-sm text-gray-500">
              Available Monday-Friday, 8am-5pm. After hours? Submit online anytime.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Preview */}
      <section className="relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-6 py-10 sm:py-16">
        <div className="mb-10 text-center">
          <h2 className="mb-3 text-3xl font-bold text-gray-900 sm:text-4xl">
            Common Questions
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {[
            {
              question: "Is Early Intervention really free?",
              answer: "Yes! Early Intervention services are provided at no cost to families. This includes evaluations, service coordination, and therapy services."
            },
            {
              question: "Do I need a doctor's referral?",
              answer: "No, you don't need a doctor's referral. Anyone can make a referral, including parents, family members, and caregivers."
            },
            {
              question: "What if my child isn't eligible?",
              answer: "If your child doesn't qualify, we'll help connect you with other community resources and support services that may be helpful."
            },
            {
              question: "Will this go on my child's record?",
              answer: "All information is confidential and protected by privacy laws. It will not affect school enrollment or future opportunities."
            }
          ].map((faq, idx) => (
            <div
              key={idx}
              className="rounded-2xl border border-white/60 bg-white/70 p-6 shadow-lg backdrop-blur-sm"
            >
              <h3 className="mb-2 text-lg font-bold text-gray-900">{faq.question}</h3>
              <p className="text-sm leading-relaxed text-gray-600">{faq.answer}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <Button
            variant="outline"
            className="h-12 px-6"
            onClick={() => (window.location.href = "/faq")}
          >
            View All Questions
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/60 bg-white/60 backdrop-blur-xl">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 py-10 sm:py-12">
          <div className="grid grid-cols-2 gap-6 sm:gap-8 lg:grid-cols-4">
            <div className="col-span-2 sm:col-span-1">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600">
                  <Heart className="h-5 w-5 text-white" />
                </div>
                <div className="leading-tight">
                  <div className="text-base font-bold text-gray-900">Connectory</div>
                  <div className="text-xs font-medium text-emerald-600">Early Intervention</div>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                Supporting children and families through developmental milestones.
              </p>
            </div>
            <div>
              <h3 className="mb-3 text-sm font-bold text-gray-900">Get Started</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><a href="/referral" className="transition-colors hover:text-gray-900">Make a Referral</a></li>
                <li><a href="#when-to-refer" className="transition-colors hover:text-gray-900">When to Refer</a></li>
                <li><a href="/faq" className="transition-colors hover:text-gray-900">FAQ</a></li>
              </ul>
            </div>
            <div>
              <h3 className="mb-3 text-sm font-bold text-gray-900">Resources</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><a href="/milestones" className="transition-colors hover:text-gray-900">Developmental Milestones</a></li>
                <li><a href="/services" className="transition-colors hover:text-gray-900">Available Services</a></li>
                <li><a href="/resources" className="transition-colors hover:text-gray-900">Family Resources</a></li>
              </ul>
            </div>
            <div>
              <h3 className="mb-3 text-sm font-bold text-gray-900">Contact</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-emerald-600" />
                  <a href="tel:1-000-000-0000" className="transition-colors hover:text-gray-900">1-000-000-0000</a>
                </li>
                <li><a href="/provider-login" className="transition-colors hover:text-gray-900">Provider Login</a></li>
                <li><a href="/privacy" className="transition-colors hover:text-gray-900">Privacy Policy</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-12 border-t border-gray-200 pt-8 text-center text-sm text-gray-600">
            <p>© {new Date().getFullYear()} Connectory Health. All rights reserved.</p>
            <p className="mt-2 text-xs">
              Early Intervention services are provided in accordance with IDEA Part C federal regulations.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}